import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { DualTrackRevenue, TenantKpi } from '@/lib/types';
import { Card, PageHeader, cellStyle, thStyle } from '@/components/ui';
import { useSort, SortTh } from '@/components/sortable';
import { useAuth } from '@/store/auth';
import { ntd } from '@/lib/format';
import { STAGE_LABEL } from '@/lib/format';
import { downloadCsv } from '@/lib/csv';
import { Icon, I } from '@/components/icons';

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="card" style={{ padding: 18, flex: 1 }}>
      <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{label}</div>
      <div className="num" style={{ fontSize: 24, fontWeight: 700, color: 'var(--ink)', marginTop: 4 }}>{value}</div>
      {hint && <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

export function ReportsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'TENANT_ADMIN' || user?.role === 'STORE_MANAGER';
  const revenue = useQuery({ queryKey: ['fin-revenue'], enabled: isAdmin, retry: false, queryFn: () => api.get<DualTrackRevenue>('/finance/revenue') });
  const kpis = useQuery({ queryKey: ['fin-kpis'], queryFn: () => api.get<TenantKpi>('/finance/kpis') });

  const r = revenue.data;
  const k = kpis.data;

  const stageRows = Object.entries(k?.byStage ?? {}).map(([stage, count]) => ({ stage, count }));
  const sortStages = useSort(stageRows, {
    stage: (s) => STAGE_LABEL[s.stage] ?? s.stage,
    count: (s) => s.count,
  });

  const exportCsv = () => {
    if (!r || !k) return;
    const rows = [
      { 項目: '營運淨現金流', 金額: r.netOperationalCents / 100 },
      { 項目: '營運儲值', 金額: r.operationalDepositsCents / 100 },
      { 項目: '移轉儲值（不計營收）', 金額: r.migrationDepositsCents / 100 },
      { 項目: '核銷金額', 金額: r.consumedCents / 100 },
      { 項目: '退款', 金額: r.refundedCents / 100 },
      { 項目: '顧客數', 金額: k.customers },
      { 項目: '移轉顧客數', 金額: k.migrationCustomers },
      { 項目: '預約總數', 金額: k.appointments },
      ...stageRows.map((s) => ({ 項目: `預約-${STAGE_LABEL[s.stage] ?? s.stage}`, 金額: s.count })),
    ];
    downloadCsv(`財務報表_${new Date().toISOString().slice(0, 10)}`, rows, ['項目', '金額']);
  };

  if (!isAdmin) {
    return (
      <div>
        <PageHeader title="財務報表" subtitle="全院營收僅租戶管理者可檢視" />
        <Card title="權限不足"><div style={{ padding: 12, color: 'var(--ink-3)', fontSize: 13 }}>全院財務營收屬機密,僅租戶管理者（TENANT_ADMIN）可檢視。</div></Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="財務報表"
        subtitle="雙軌財務：移轉資料儲值不計入新收現金流；營運淨額 = 營運儲值 − 營運退款"
        right={<button className="btn btn-ghost" disabled={!r || !k} onClick={exportCsv}><Icon d={I.download} size={13} /> 匯出 CSV</button>}
      />

      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        <Stat label="營運淨現金流" value={r ? ntd(r.netOperationalCents) : '—'} hint="排除移轉資料" />
        <Stat label="營運儲值" value={r ? ntd(r.operationalDepositsCents) : '—'} />
        <Stat label="移轉儲值（不計營收）" value={r ? ntd(r.migrationDepositsCents) : '—'} />
        <Stat label="退款" value={r ? ntd(r.refundedCents) : '—'} />
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        <Stat label="顧客數" value={k ? String(k.customers) : '—'} hint={k ? `含移轉 ${k.migrationCustomers}` : undefined} />
        <Stat label="預約總數" value={k ? String(k.appointments) : '—'} />
        <Stat label="核銷金額" value={r ? ntd(r.consumedCents) : '—'} />
      </div>

      <Card title="預約階段分布">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><SortTh label="階段" sortKey="stage" sort={sortStages} /><SortTh label="數量" sortKey="count" sort={sortStages} /></tr></thead>
          <tbody>
            {sortStages.sorted.map(({ stage, count }) => (
              <tr key={stage}>
                <td style={cellStyle}>{STAGE_LABEL[stage] ?? stage}</td>
                <td style={cellStyle} className="num">{count}</td>
              </tr>
            ))}
            {k && Object.keys(k.byStage).length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={2}>尚無預約</td></tr>}
          </tbody>
        </table>
      </Card>

      <UnitEconomics />
      <CustomerRetention />
      <SafetySignals />
    </div>
  );
}

interface Safety {
  complaint: { total: number; ratePer100Done: number; open: number; overdue: number; severity: { level: number; count: number }[] };
  contraindication: { blocks: number; topHits: { tag: string; count: number }[]; byTreatment: { name: string; count: number }[] };
  outcome: { sessions: number; documented: number; docRatePct: number };
}

function SafetySignals() {
  const q = useQuery({ queryKey: ['safety-signals'], retry: false, queryFn: () => api.get<Safety>('/finance/safety-signals') });
  const d = q.data;
  if (!d) return null;
  return (
    <>
      <div style={{ display: 'flex', gap: 16, margin: '16px 0' }}>
        <Stat label="客訴數" value={String(d.complaint.total)} hint={`每 100 完工 ${d.complaint.ratePer100Done} 件`} />
        <Stat label="處理中客訴" value={String(d.complaint.open)} hint={`逾期 ${d.complaint.overdue}`} />
        <Stat label="禁忌攔截" value={String(d.contraindication.blocks)} hint="開單即時擋下" />
        <Stat label="成效紀錄完整率" value={`${d.outcome.docRatePct}%`} hint={`${d.outcome.documented}/${d.outcome.sessions} 組含前後對比`} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Card title="客訴嚴重度分布" icon={<Icon d={I.shield} size={14} style={{ color: 'var(--warn)' }} />}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {d.complaint.severity.map((s) => {
              const max = Math.max(1, ...d.complaint.severity.map((x) => x.count));
              return (
                <div key={s.level} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 48, fontSize: 11.5, color: 'var(--ink-2)' }}>L{s.level}</span>
                  <div style={{ flex: 1, height: 8, background: 'var(--surface-2)', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ width: `${(s.count / max) * 100}%`, height: '100%', background: s.level >= 4 ? 'var(--danger)' : 'var(--warn)' }} />
                  </div>
                  <span style={{ width: 26, textAlign: 'right', fontSize: 12, fontWeight: 600 }} className="num">{s.count}</span>
                </div>
              );
            })}
          </div>
        </Card>

        <Card title="禁忌攔截熱點(醫療安全)" icon={<Icon d={I.alert} size={14} style={{ color: 'var(--danger)' }} />}>
          {d.contraindication.blocks === 0 ? (
            <div style={{ padding: 12, color: 'var(--ink-4)', fontSize: 13 }}>尚無禁忌攔截事件 —— 開單命中顧客禁忌症時會自動留痕於此。</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={thStyle}>命中禁忌</th><th style={{ ...thStyle, textAlign: 'right' }}>次數</th><th style={thStyle}>主要療程</th></tr></thead>
              <tbody>
                {d.contraindication.topHits.map((h, i) => (
                  <tr key={h.tag}>
                    <td style={{ ...cellStyle, fontWeight: 600 }}>{h.tag}</td>
                    <td style={{ ...cellStyle, textAlign: 'right', color: 'var(--danger)', fontWeight: 700 }} className="num">{h.count}</td>
                    <td style={cellStyle}>{d.contraindication.byTreatment[i]?.name ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </>
  );
}

interface Retention {
  summary: { customers: number; withVisits: number; active30: number; active90: number; dormant: number; churnRatePct: number; avgDaysBetweenVisits: number };
  cohorts: { cohort: string; size: number; retained30: number; retained60: number; retained90: number }[];
  sleeping: { customerId: string; name: string; phone: string; tier: string; visits: number; lastVisit: string; daysSince: number }[];
}

function CustomerRetention() {
  const q = useQuery({ queryKey: ['retention'], retry: false, queryFn: () => api.get<Retention>('/finance/retention') });
  const d = q.data;
  if (!d) return null;
  const s = d.summary;
  return (
    <>
      <div style={{ display: 'flex', gap: 16, margin: '16px 0' }}>
        <Stat label="活躍顧客(30 天)" value={String(s.active30)} hint={`/ ${s.withVisits} 有到診`} />
        <Stat label="沉睡(>90 天)" value={String(s.dormant)} />
        <Stat label="流失率" value={`${s.churnRatePct}%`} hint="沉睡 / 有到診顧客" />
        <Stat label="平均回診間隔" value={`${s.avgDaysBetweenVisits} 天`} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 16 }}>
        <Card title="Cohort 回訪曲線(依首訪月)" icon={<Icon d={I.chart} size={14} style={{ color: 'var(--primary)' }} />}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={thStyle}>首訪月</th><th style={{ ...thStyle, textAlign: 'right' }}>人數</th><th style={{ ...thStyle, textAlign: 'right' }}>30天</th><th style={{ ...thStyle, textAlign: 'right' }}>60天</th><th style={{ ...thStyle, textAlign: 'right' }}>90天</th></tr></thead>
            <tbody>
              {d.cohorts.map((c) => (
                <tr key={c.cohort}>
                  <td style={cellStyle} className="num">{c.cohort}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{c.size}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{c.retained30}%</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{c.retained60}%</td>
                  <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 700, color: 'var(--primary)' }} className="num">{c.retained90}%</td>
                </tr>
              ))}
              {d.cohorts.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={5}>尚無資料</td></tr>}
            </tbody>
          </table>
        </Card>

        <Card title={`沉睡顧客喚回名單(${d.sleeping.length})`} icon={<Icon d={I.bell} size={14} style={{ color: 'var(--warn)' }} />}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={thStyle}>顧客</th><th style={thStyle}>電話</th><th style={{ ...thStyle, textAlign: 'right' }}>到診次數</th><th style={{ ...thStyle, textAlign: 'right' }}>未回診</th></tr></thead>
            <tbody>
              {d.sleeping.map((c) => (
                <tr key={c.customerId}>
                  <td style={{ ...cellStyle, fontWeight: 600 }}>{c.name}{c.tier === 'VIP' && <span style={{ color: 'var(--warn)', marginLeft: 4 }}>★</span>}</td>
                  <td style={cellStyle} className="num">{c.phone}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{c.visits}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', color: 'var(--danger)' }} className="num">{c.daysSince} 天</td>
                </tr>
              ))}
              {d.sleeping.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={4}>無沉睡顧客 👍</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>
    </>
  );
}

interface TxEcon { id: string; name: string; priceCents: number; cogsCents: number; marginCents: number; marginPct: number; doneCount: number; grossProfitCents: number; costKnown: boolean }
interface UnitEco { treatments: TxEcon[]; customer: { customers: number; avgRevenuePerCustomerCents: number; repeatRatePct: number; avgVisits: number } }

function UnitEconomics() {
  const q = useQuery({ queryKey: ['unit-economics'], retry: false, queryFn: () => api.get<UnitEco>('/finance/unit-economics') });
  const d = q.data;
  if (!d) return null;
  const c = d.customer;
  return (
    <>
      <div style={{ display: 'flex', gap: 16, margin: '16px 0' }}>
        <Stat label="客單(LTV 代理)" value={ntd(c.avgRevenuePerCustomerCents)} hint="每顧客平均營運儲值" />
        <Stat label="回購率" value={`${c.repeatRatePct}%`} hint="≥2 次到診" />
        <Stat label="平均回診次數" value={String(c.avgVisits)} hint="非取消預約 / 顧客" />
        <Stat label="顧客數" value={String(c.customers)} />
      </div>

      <Card title="每療程毛利(售價 − 耗材成本)" icon={<Icon d={I.trend} size={14} style={{ color: 'var(--primary)' }} />}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={thStyle}>療程</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>售價</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>耗材成本</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>單筆毛利</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>毛利率</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>完工數</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>毛利貢獻</th>
          </tr></thead>
          <tbody>
            {d.treatments.map((t) => (
              <tr key={t.id}>
                <td style={{ ...cellStyle, fontWeight: 600 }}>{t.name}</td>
                <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{ntd(t.priceCents)}</td>
                <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{t.costKnown ? ntd(t.cogsCents) : <span style={{ color: 'var(--warn)' }}>未設定</span>}</td>
                <td style={{ ...cellStyle, textAlign: 'right', color: t.marginCents < 0 ? 'var(--danger)' : 'var(--ink)' }} className="num">{ntd(t.marginCents)}</td>
                <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 700, color: t.marginPct < 0 ? 'var(--danger)' : 'var(--primary)' }} className="num">{t.marginPct}%</td>
                <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{t.doneCount}</td>
                <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 700 }} className="num">{ntd(t.grossProfitCents)}</td>
              </tr>
            ))}
            {d.treatments.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={7}>尚無療程</td></tr>}
          </tbody>
        </table>
        <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 8 }}>「耗材成本」由扣料規則 × 耗材單位成本推導;顯示「未設定」者請至庫存頁填單位成本並設定扣料規則。</div>
      </Card>
    </>
  );
}
